/**
 * 产康预约：开始时段点选（15 分钟格），与 technician-day 占用、班次 [上班,下班] 联动禁选/标色
 * 有班次时：仅允许在上班～下班内开始且整段服务不晚于下班；此区间以外不可选
 *
 * 单格 isDisabled 判定顺序（满足任一则禁选，按此序短路语义 / tooltip）：
 * 1. 未选技师
 * 2. 今天已过去的格
 * 3. 班次 / 表盘可约窗口外
 * 4. 午休/休息：整段服务与 [restStart, restEnd) 重叠则禁选
 * 5. 当日提前预约量
 * 6. 服务时长无效
 * 7. 与已占用强禁选（整段服务与已排期重叠，且 disableSlotsOverlappingBooked）
 */
import React from 'react'
import PropTypes from 'prop-types'
import { Alert } from 'antd'
import dayjs from 'dayjs'
import {
  appointmentDatePart,
  isValidServiceMinutes,
  normalizeServiceMinutes,
  serviceWindowOverlapsBookedRanges,
  isFifteenMinuteSlotBusy
} from '@/utils/renderUtils'
import {
  BOOKING_LEAD_MINUTES,
  BUSINESS_END_MINUTES,
  getPastSlotCountForToday,
  SLOTS_BY_HOUR,
  SLOT_START_MINUTES,
  SLOTS_PER_ROW
} from '@/utils/technicianBookingTimeSlots'

const toDayjs = (value) => (value ? dayjs(value) : null)

const minutesFromMidnight = (d = dayjs()) => d.hour() * 60 + d.minute()

/**
 * @param {number} serviceTimeMinutes 项目服务时长（分钟）
 * @param {{start:number,end:number}[]} [bookedRanges] 技师已占用（预约）
 * @param {number|null} [shiftStartMinutes] 上班时间（分钟）
 * @param {number|null} [shiftEndMinutes] 下班时间（分钟）
 * @param {number|null} [restStartMinutes] 午休/休息开始（分钟，与 restEnd 成对）
 * @param {number|null} [restEndMinutes] 午休/休息结束（半开区间右端，该时刻起可约）
 * @param {boolean} [disableUntilTechnicianSelected] 未选技师时全部禁选
 * @param {boolean} [applyBookingLeadConstraint] 预约日「今天」须满足提前约（见 technicianBookingTimeSlots.BOOKING_LEAD_MINUTES）
 * @param {boolean} [disableSlotsOverlappingBooked] 与已排期整段重叠时是否强禁选
 */
const StartTimeSlotSelect = ({
  value,
  onChange,
  disabledDate,
  serviceTimeMinutes,
  bookedRanges = [],
  shiftStartMinutes = null,
  shiftEndMinutes = null,
  restStartMinutes = null,
  restEndMinutes = null,
  disableUntilTechnicianSelected = false,
  applyBookingLeadConstraint = true,
  disableSlotsOverlappingBooked = true
}) => {
  const pastSlotCount = getPastSlotCountForToday(disabledDate)
  const serviceMins = normalizeServiceMinutes(serviceTimeMinutes)
  const hasValidDuration = isValidServiceMinutes(serviceMins)

  const appointmentDay = toDayjs(disabledDate)
  const isAppointmentToday = appointmentDay?.isValid() && appointmentDay.isSame(dayjs(), 'day')
  const leadCutoffMinutes =
    applyBookingLeadConstraint && isAppointmentToday
      ? minutesFromMidnight() + BOOKING_LEAD_MINUTES
      : null
  const datePart = appointmentDatePart(disabledDate)

  /**
   * 可约下界：无上班数据时＝表盘首格 9:30；有则 max(9:30, 上班) —— 早于下界的格应禁选
   * 可约上界：无下班数据时＝表盘 18:00；有则用工 —— 整段服务超上界应禁选
   */
  const effectiveEndMinutes =
    shiftEndMinutes != null ? shiftEndMinutes : BUSINESS_END_MINUTES
  const effectiveStartMinutes =
    shiftStartMinutes != null
      ? Math.max(SLOT_START_MINUTES, shiftStartMinutes)
      : SLOT_START_MINUTES

  return (
    <div className="form-item" style={{ width: '100%' }}>
      {disableUntilTechnicianSelected
        ? (
        <Alert
          type="info"
          showIcon
          message="请先选择技师"
          description="先在上方选定技师，再选开始时间；未选技师前下方时段不可点。"
          style={{ marginBottom: 12 }}
        />
          )
        : null}
      <div className="time-slots-wrap" style={{ '--slots-per-row': SLOTS_PER_ROW }}>
        {SLOTS_BY_HOUR.map((rowSlots, rowIdx) => {
          const hourLabel = rowSlots[0]?.label ?? String(rowIdx)
          return (
            <React.Fragment key={hourLabel}>
              <span className="time-slot-hour-label">{hourLabel}</span>
              {rowSlots.map(({ label, value: slotVal, index, startMinutes }) => {
                // --- 1. 未选技师：见 disableUntilTechnicianSelected（全表 Alert）---
                // --- 2. 今天已过去的格 ---
                const isPastSlot = pastSlotCount >= 0 && index < pastSlotCount
                // --- 3. 班次/表盘可约窗口外：早于有效下界 或 整段服务晚于有效上界（下界与 shiftStart+表盘首格 一致）---
                const outsideWorkWindow =
                  startMinutes < effectiveStartMinutes ||
                  (hasValidDuration && startMinutes + serviceMins > effectiveEndMinutes)
                // --- 4. 午休：整段 [start, start+duration) 与 [restStart, restEnd) 有交集则禁选 ---
                const overlapsRest =
                  hasValidDuration &&
                  restStartMinutes != null &&
                  restEndMinutes != null &&
                  startMinutes < restEndMinutes &&
                  startMinutes + serviceMins > restStartMinutes
                // --- 5. 当日提前预约量（仅预约日=今天且 applyBookingLeadConstraint）---
                const tooSoonForLead =
                  leadCutoffMinutes != null && startMinutes < leadCutoffMinutes
                // --- 6. 服务时长：hasValidDuration（!hasValidDuration 时全格禁选）---
                // --- 7. 与已占用强禁选 ---
                const slotStartMs = +dayjs(`${datePart} ${slotVal}`, 'YYYY-MM-DD HH:mm', true)
                const serviceDurationMs = hasValidDuration ? serviceMins * 60 * 1000 : 0
                const isOverlapBooked =
                  !disableUntilTechnicianSelected &&
                  Number.isFinite(slotStartMs) &&
                  hasValidDuration &&
                  serviceWindowOverlapsBookedRanges(slotStartMs, serviceDurationMs, bookedRanges)
                const bookedConflict = disableSlotsOverlappingBooked && isOverlapBooked
                const isExistingWork =
                  !disableUntilTechnicianSelected &&
                  Number.isFinite(slotStartMs) &&
                  bookedRanges.length > 0 &&
                  isFifteenMinuteSlotBusy(slotStartMs, bookedRanges)

                const isDisabled =
                  disableUntilTechnicianSelected ||
                  isPastSlot ||
                  outsideWorkWindow ||
                  overlapsRest ||
                  tooSoonForLead ||
                  !hasValidDuration ||
                  bookedConflict
                const isSelected = value === slotVal
                const pickSlot = () => {
                  if (!isDisabled) onChange(slotVal)
                }
                /** tooltip 与 isDisabled 顺序 1～6 对齐；仅标色不单独禁选见 isExistingWork */
                let slotTitle
                if (disableUntilTechnicianSelected) {
                  slotTitle = '请先选择技师'
                } else if (isPastSlot) {
                  slotTitle = '今日该时段已过'
                } else if (outsideWorkWindow) {
                  slotTitle = '不在当班可约时间内（上班～下班）'
                } else if (overlapsRest) {
                  slotTitle = '该时段与午休/休息重叠，请选其他时间'
                } else if (tooSoonForLead) {
                  slotTitle = `当日须至少提前 ${BOOKING_LEAD_MINUTES} 分钟可约`
                } else if (!hasValidDuration) {
                  slotTitle = '缺少有效服务时长'
                } else if (bookedConflict) {
                  slotTitle = '以该时刻为开始，整段服务与已有工作重叠'
                } else if (isExistingWork) {
                  slotTitle = '该时段为已有工作'
                } else {
                  slotTitle = undefined
                }
                let toneClass = ''
                if (isOverlapBooked) {
                  toneClass = 'time-slot-btn--overlap'
                } else if (isExistingWork) {
                  toneClass = 'time-slot-btn--busy'
                }
                return (
                  <span
                    key={slotVal}
                    role="button"
                    tabIndex={isDisabled ? -1 : 0}
                    aria-disabled={isDisabled}
                    title={slotTitle}
                    className={`time-slot-btn ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''} ${toneClass}`.trim()}
                    onClick={pickSlot}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        pickSlot()
                      }
                    }}
                  >
                    {label}
                  </span>
                )
              })}
            </React.Fragment>
          )
        })}
      </div>
      {bookedRanges.length > 0 && !disableUntilTechnicianSelected
        ? (
        <div className="time-slot-legend" role="note">
          <div className="time-slot-legend__row">
            <span
              className="time-slot-legend__swatch time-slot-legend__swatch--busy"
              aria-hidden="true"
            />
            <span>橙色：已有工作</span>
          </div>
          <div className="time-slot-legend__row">
            <span
              className="time-slot-legend__swatch time-slot-legend__swatch--overlap"
              aria-hidden="true"
            />
            <span>
              红色：工作重叠（以该格为开始时间，整段服务与已有工作相交
              {disableSlotsOverlappingBooked ? '，此类时刻不可选' : '；可照常点选，提交以服务端校验为准' }
              ）
            </span>
          </div>
        </div>
          )
        : null}
      {isAppointmentToday && applyBookingLeadConstraint
        ? (
        <div className="form-hint form-hint-muted" style={{ marginTop: 8 }}>
          当日预约需至少提前 {BOOKING_LEAD_MINUTES} 分钟
        </div>
          )
        : null}
    </div>
  )
}

StartTimeSlotSelect.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  disabledDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  serviceTimeMinutes: PropTypes.number,
  bookedRanges: PropTypes.arrayOf(
    PropTypes.shape({ start: PropTypes.number, end: PropTypes.number })
  ),
  shiftStartMinutes: PropTypes.number,
  shiftEndMinutes: PropTypes.number,
  restStartMinutes: PropTypes.number,
  restEndMinutes: PropTypes.number,
  disableUntilTechnicianSelected: PropTypes.bool,
  applyBookingLeadConstraint: PropTypes.bool,
  disableSlotsOverlappingBooked: PropTypes.bool
}

export default StartTimeSlotSelect
